/**
 * Floating toggle button component.
 * Shows the launcher icon when closed, X when open.
 *
 * Shape, icon, custom image and text label are config-driven. An empty
 * launcher config renders the default: the gold speech-bubble glyph that
 * crossfades to a chevron when open.
 */

// Close icon SVG - fixed X, inherits the button's `color` (white).
const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
</svg>`;

// Default launcher: gold speech-bubble glyph that crossfades to a chevron
// when open. Both stroke=currentColor so --launcher-icon-color tints the
// chat glyph; the chevron picks up its own ink colour from CSS.
const LAUNCHER_CHAT_GLYPH = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none">
  <path d="M4 11.5C4 7.36 7.58 4 12 4h2c4.42 0 8 3.36 8 7.5S18.42 19 14 19h-1.2L8.2 22v-3.3C5.66 17.5 4 14.7 4 11.5z" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
  <circle cx="10" cy="11.5" r="1.05" fill="currentColor"/>
  <circle cx="13.5" cy="11.5" r="1.05" fill="currentColor"/>
  <circle cx="17" cy="11.5" r="1.05" fill="currentColor"/>
</svg>`;

const LAUNCHER_CHEVRON_GLYPH = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22" fill="none">
  <path d="M6 9l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

// The original Lizadocs launcher mark: overlapping binary chat bubbles with
// 1010/0110 digits. Brand-fixed fills (#420b6e purple, #ff9800 orange), so
// launcher_icon_color deliberately does not tint it - recolouring would
// destroy the mark. Every other built-in stays single-hue and tintable.
const CLASSIC_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
  <g id="Layer_3">
    <path d="m6.5 14.5a2 2 0 0 1 -2-2v-6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-7l-3 2z" fill="#420b6e"/>
    <path d="m18.5 6.5v6a2 2 0 0 1 -2 2h-6.984a2 2 0 0 0 1.984 2h4l3 2v-2a2 2 0 0 0 2-2v-6a2.008 2.008 0 0 0 -2-2z" fill="#ff9800"/>
  </g>
  <g id="Layer_2_copy">
    <path d="m6 16.5v-1.55a2.5 2.5 0 0 1 -2-2.45v-6a2.5 2.5 0 0 1 2.5-2.5h10a2.5 2.5 0 0 1 2.5 2.5v6a2.5 2.5 0 0 1 -2.5 2.5h-6.849l-2.874 1.916a.5.5 0 0 1 -.777-.416zm.5-11.5a1.5 1.5 0 0 0 -1.5 1.5v6a1.5 1.5 0 0 0 1.5 1.5.5.5 0 0 1 .5.5v1.066l2.223-1.482a.5.5 0 0 1 .277-.084h7a1.5 1.5 0 0 0 1.5-1.5v-6a1.5 1.5 0 0 0 -1.5-1.5z" fill="#420b6e"/>
    <path d="m18.223 18.916-2.874-1.916h-3.849a2.5 2.5 0 0 1 -2.5-2.5.5.5 0 0 1 1 0 1.5 1.5 0 0 0 1.5 1.5h4a.5.5 0 0 1 .277.084l2.223 1.482v-1.066a.5.5 0 0 1 .5-.5 1.5 1.5 0 0 0 1.5-1.5v-6a1.5 1.5 0 0 0 -1.5-1.5.5.5 0 0 1 0-1 2.5 2.5 0 0 1 2.5 2.5v6a2.5 2.5 0 0 1 -2 2.45v1.55a.5.5 0 0 1 -.777.416z" fill="#420b6e"/>
    <path d="m6.5 9a.5.5 0 0 1 -.5-.5v-2a.5.5 0 0 1 1 0v2a.5.5 0 0 1 -.5.5z" fill="#ff9800"/>
    <path d="m9.75 9h-.5a1.252 1.252 0 0 1 -1.25-1.25v-.5a1.252 1.252 0 0 1 1.25-1.25h.5a1.252 1.252 0 0 1 1.25 1.25v.5a1.252 1.252 0 0 1 -1.25 1.25zm-.5-2a.25.25 0 0 0 -.25.25v.5a.25.25 0 0 0 .25.25h.5a.25.25 0 0 0 .25-.25v-.5a.25.25 0 0 0 -.25-.25z" fill="#ff9800"/>
    <path d="m12.5 9a.5.5 0 0 1 -.5-.5v-2a.5.5 0 0 1 1 0v2a.5.5 0 0 1 -.5.5z" fill="#ff9800"/>
    <path d="m10.5 13a.5.5 0 0 1 -.5-.5v-2a.5.5 0 0 1 1 0v2a.5.5 0 0 1 -.5.5z" fill="#ff9800"/>
    <path d="m12.5 13a.5.5 0 0 1 -.5-.5v-2a.5.5 0 0 1 1 0v2a.5.5 0 0 1 -.5.5z" fill="#ff9800"/>
    <path d="m15.75 9h-.5a1.252 1.252 0 0 1 -1.25-1.25v-.5a1.252 1.252 0 0 1 1.25-1.25h.5a1.252 1.252 0 0 1 1.25 1.25v.5a1.252 1.252 0 0 1 -1.25 1.25zm-.5-2a.25.25 0 0 0 -.25.25v.5a.25.25 0 0 0 .25.25h.5a.25.25 0 0 0 .25-.25v-.5a.25.25 0 0 0 -.25-.25z" fill="#ff9800"/>
    <path d="m7.75 13h-.5a1.252 1.252 0 0 1 -1.25-1.25v-.5a1.252 1.252 0 0 1 1.25-1.25h.5a1.252 1.252 0 0 1 1.25 1.25v.5a1.252 1.252 0 0 1 -1.25 1.25zm-.5-2a.25.25 0 0 0 -.25.25v.5a.25.25 0 0 0 .25.25h.5a.25.25 0 0 0 .25-.25v-.5a.25.25 0 0 0 -.25-.25z" fill="#ff9800"/>
    <path d="m15.75 13h-.5a1.252 1.252 0 0 1 -1.25-1.25v-.5a1.252 1.252 0 0 1 1.25-1.25h.5a1.252 1.252 0 0 1 1.25 1.25v.5a1.252 1.252 0 0 1 -1.25 1.25zm-.5-2a.25.25 0 0 0 -.25.25v.5a.25.25 0 0 0 .25.25h.5a.25.25 0 0 0 .25-.25v-.5a.25.25 0 0 0 -.25-.25z" fill="#ff9800"/>
  </g>
</svg>`;

// Built-in icons share one construction: the default glyph's bubble
// silhouette (or an equally weighted shape) as a soft currentColor fill with
// a 1.7 stroke, plus one solid-fill accent detail - the layered logic of the
// classic mark, kept single-hue so --launcher-icon-color tints everything.
const BUILTIN_ICONS: Record<string, string> = {
  classic: CLASSIC_ICON,
  chat: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none">
    <path d="M3.5 12.2C3.5 8.5 6.7 5.8 10.6 5.8h3.3c3.9 0 7.1 2.7 7.1 6.4 0 3.7-3.2 6.4-7.1 6.4h-2.2L7.6 21.4v-3.1c-2.4-1.1-4.1-3.3-4.1-6.1z" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M8.3 10.9h7.4M8.3 13.7h4.6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
    <circle cx="21" cy="6" r="2.6" fill="currentColor"/>
  </svg>`,
  help: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none">
    <path d="M4 11.5C4 7.36 7.58 4 12 4h2c4.42 0 8 3.36 8 7.5S18.42 19 14 19h-1.2L8.2 22v-3.3C5.66 17.5 4 14.7 4 11.5z" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M10.7 10c.25-1.3 1.35-2.1 2.6-2.1 1.4 0 2.6.95 2.6 2.2 0 1.7-2.2 1.9-2.4 3.4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
    <circle cx="13.5" cy="16.2" r="1.05" fill="currentColor"/>
  </svg>`,
  message: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none">
    <rect x="3.5" y="6.5" width="19" height="13.5" rx="3" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.7"/>
    <path d="M5.5 9.3l6.4 4.5c.66.46 1.54.46 2.2 0l6.4-4.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="21" cy="6" r="2.6" fill="currentColor"/>
  </svg>`,
  sparkle: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26" fill="none">
    <path d="M12 4.8l1.77 4.9c.18.5.57.89 1.07 1.07l4.9 1.77-4.9 1.77c-.5.18-.89.57-1.07 1.07L12 20.2l-1.77-4.9c-.18-.5-.57-.89-1.07-1.07l-4.9-1.77 4.9-1.77c.5-.18.89-.57 1.07-1.07L12 4.8z" fill="currentColor" fill-opacity="0.16" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M20.2 4.2l.72 1.96 1.96.72-1.96.72-.72 1.96-.72-1.96-1.96-.72 1.96-.72.72-1.96z" fill="currentColor"/>
    <circle cx="20.6" cy="17.6" r="1.5" fill="currentColor"/>
  </svg>`,
};

export type LauncherShape = "rounded_square" | "circle";
export type LauncherIcon =
  | "default"
  | "classic"
  | "chat"
  | "help"
  | "sparkle"
  | "message"
  | "custom";

export interface LauncherOptions {
  shape?: LauncherShape;
  icon?: LauncherIcon;
  iconUrl?: string;
  label?: string;
}

export interface FloatingButtonProps {
  isOpen: boolean;
  onClick: () => void;
  launcher?: LauncherOptions;
}

export function createFloatingButton(
  props: FloatingButtonProps,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.className = "toggle-button";
  button.addEventListener("click", props.onClick);
  renderLauncher(button, props.isOpen, props.launcher);
  return button;
}

export function updateFloatingButton(
  button: HTMLButtonElement,
  isOpen: boolean,
  launcher?: LauncherOptions,
): void {
  renderLauncher(button, isOpen, launcher);
}

function renderLauncher(
  button: HTMLButtonElement,
  isOpen: boolean,
  launcher?: LauncherOptions,
): void {
  const shape = launcher?.shape ?? "rounded_square";
  const icon = launcher?.icon ?? "default";
  const label = launcher?.label?.trim();
  // A text label turns the launcher into a pill and overrides the shape,
  // but only in the closed state - the open state is always the bare X.
  const isPill = !isOpen && Boolean(label);
  // Only the default launcher (no label, built-in default icon) morphs
  // chat→chevron via the crossfade. Every other launcher keeps the legacy
  // open→X swap untouched.
  const isMorphingDefault = icon === "default" && !label;

  button.classList.remove("launcher--circle", "launcher--pill");
  if (isPill) {
    button.classList.add("launcher--pill");
  } else if (shape === "circle") {
    button.classList.add("launcher--circle");
  }

  button.classList.toggle("is-open", isMorphingDefault && isOpen);

  if (isOpen) {
    button.setAttribute("aria-label", "Close chat");
  } else if (label) {
    // Visible label already names the button; an aria-label would shadow it.
    button.removeAttribute("aria-label");
  } else {
    button.setAttribute("aria-label", "Open chat");
  }

  // The default launcher renders both glyphs always; CSS crossfades them
  // based on the is-open class, so the same markup serves open and closed.
  if (isMorphingDefault) {
    button.innerHTML = `<span class="launcher-glyph launcher-glyph--chat" aria-hidden="true">${LAUNCHER_CHAT_GLYPH}</span><span class="launcher-glyph launcher-glyph--chevron" aria-hidden="true">${LAUNCHER_CHEVRON_GLYPH}</span>`;
    return;
  }

  // Non-default launchers keep the original single-SVG markup so existing
  // widgets render byte-for-byte as before.
  if (isOpen) {
    button.innerHTML = CLOSE_ICON;
    return;
  }

  button.replaceChildren();

  if (icon === "custom" && launcher?.iconUrl) {
    const image = document.createElement("img");
    image.src = launcher.iconUrl;
    image.alt = "";
    image.className = "launcher-custom-icon";
    button.appendChild(image);
  } else {
    const iconWrapper = document.createElement("span");
    iconWrapper.className = "launcher-icon";
    iconWrapper.innerHTML = BUILTIN_ICONS[icon] ?? LAUNCHER_CHAT_GLYPH;
    button.appendChild(iconWrapper);
  }

  if (label) {
    const labelElement = document.createElement("span");
    labelElement.className = "launcher-label";
    labelElement.textContent = label;
    button.appendChild(labelElement);
  }
}
